import React, { useState, useRef, useEffect } from 'react';

const TimePicker = ({ value, onChange, onBlur, isValid, className, placeholder, required = false, minHour = 0, maxHour = 24 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [timeOptions, setTimeOptions] = useState([]);
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Generate time options based on minHour and maxHour (e.g., 08:00, 08:30, 09:00, 09:30, ..., 18:00, 18:30)
    useEffect(() => {
        const options = [];
        const startHour = Math.max(0, minHour);
        const endHour = Math.min(24, maxHour);

        for (let hour = startHour; hour < endHour; hour++) {
            const hourStr = hour.toString().padStart(2, '0');
            options.push(`${hourStr}:00`);
            options.push(`${hourStr}:30`);
        }
        setTimeOptions(options);
    }, [minHour, maxHour]);

    // Helper function to convert time string to minutes for comparison
    const timeToMinutes = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') return 0;
        const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return 0;
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        return hours * 60 + minutes;
    };

    // Helper function to find the closest time option to the current value
    const findClosestOptionIndex = (currentValue) => {
        if (!currentValue || timeOptions.length === 0) return -1;

        const currentMinutes = timeToMinutes(currentValue);
        let closestIndex = -1;
        let smallestDiff = Infinity;

        timeOptions.forEach((option, index) => {
            const optionMinutes = timeToMinutes(option);
            const diff = Math.abs(currentMinutes - optionMinutes);
            if (diff < smallestDiff) {
                smallestDiff = diff;
                closestIndex = index;
            }
        });

        return closestIndex;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update selected option when dropdown opens or value changes
    useEffect(() => {
        if (isOpen) {
            const closestIndex = findClosestOptionIndex(value);
            setSelectedOptionIndex(closestIndex);

            // Scroll to the selected option
            if (closestIndex >= 0 && dropdownRef.current) {
                const optionElement = dropdownRef.current.children[closestIndex];
                if (optionElement) {
                    optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
        }
    }, [isOpen, value, timeOptions]);

    const handleInputClick = () => {
        setIsOpen(true);
        // Select all text when clicked
        if (inputRef.current) {
            inputRef.current.select();
        }
    };

    const handleInputChange = (e) => {
        onChange(e);
    };

    const handleOptionSelect = (time) => {
        onChange({ target: { value: time, name: 'time' } });
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (isOpen && selectedOptionIndex >= 0) {
                // Select the highlighted option
                handleOptionSelect(timeOptions[selectedOptionIndex]);
            } else {
                setIsOpen(true);
            }
        } else if (e.key === 'Tab') {
            setIsOpen(false);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) {
                setIsOpen(true);
            } else {
                // Navigate down in dropdown
                const nextIndex = selectedOptionIndex < timeOptions.length - 1 ? selectedOptionIndex + 1 : 0;
                setSelectedOptionIndex(nextIndex);
                // Scroll to the newly selected option
                if (dropdownRef.current?.children[nextIndex]) {
                    dropdownRef.current.children[nextIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (isOpen) {
                // Navigate up in dropdown
                const prevIndex = selectedOptionIndex > 0 ? selectedOptionIndex - 1 : timeOptions.length - 1;
                setSelectedOptionIndex(prevIndex);
                // Scroll to the newly selected option
                if (dropdownRef.current?.children[prevIndex]) {
                    dropdownRef.current.children[prevIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            inputRef.current?.blur();
        }
    };

    const handleBlur = (e) => {
        // Delay blur to allow option selection
        setTimeout(() => {
            if (!dropdownRef.current?.contains(document.activeElement)) {
                setIsOpen(false);
                onBlur?.(e);
            }
        }, 150);
    };

    const baseInputClass = "w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 text-sm";
    const validClass = "border-gray-300 focus:border-blue-500 focus:ring-blue-500";
    const invalidClass = "border-red-500 focus:border-red-500 focus:ring-red-500";
    const inputClass = `${baseInputClass} ${isValid ? validClass : invalidClass} ${className || ''}`;

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                value={value || ''}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || "HH:MM"}
                required={required}
                className={inputClass}
                autoComplete="off"
            />

            {/* Dropdown arrow indicator */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
                >
                    {timeOptions.map((time, index) => (
                        <button
                            key={time}
                            type="button"
                            className={`w-full px-3 py-2 text-left text-sm border-none focus:outline-none transition-colors duration-150 ${index === selectedOptionIndex
                                ? 'bg-blue-100 text-blue-800 font-medium'
                                : 'bg-transparent hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700'
                                }`}
                            onClick={() => handleOptionSelect(time)}
                            onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                            onMouseEnter={() => setSelectedOptionIndex(index)} // Update selection on hover
                        >
                            {time}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TimePicker; 